package com.daytimegaming.ourdailyfinances.presentation.budget

import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.AccountBalance
import androidx.compose.material.icons.filled.Bolt
import androidx.compose.material.icons.filled.Category
import androidx.compose.material.icons.filled.Dashboard
import androidx.compose.material.icons.filled.Group
import androidx.compose.material.icons.filled.Tune
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.daytimegaming.ourdailyfinances.domain.model.Budget
import com.daytimegaming.ourdailyfinances.domain.model.Dashboard
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.presentation.home.BudgetProgress
import com.daytimegaming.ourdailyfinances.presentation.home.getRecentSpendingCategoryBreakdownKey
import com.daytimegaming.ourdailyfinances.presentation.preview.ViewBudgetPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.CATEGORIES
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import kotlin.math.abs
import kotlin.time.Clock

@Composable
fun ViewBudgetScreen(
    budgetId: String,
    onConfigureClick: (String) -> Unit,
    onNavigateBack: () -> Unit,
    onTransactionClick: (String) -> Unit,
    viewModel: ViewBudgetViewModel = koinViewModel(key = budgetId) { parametersOf(budgetId) }
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    ViewBudgetScreenContent(
        state = state,
        onConfigureClick = { onConfigureClick(budgetId) },
        onNavigateBack = onNavigateBack,
        onTransactionClick = onTransactionClick
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ViewBudgetScreenContent(
    state: ViewBudgetScreenState,
    onConfigureClick: () -> Unit,
    onNavigateBack: () -> Unit,
    onTransactionClick: (String) -> Unit
) {
    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = when (state) {
                            is ViewBudgetScreenState.Loaded -> state.budget.name
                            else -> "Budget Details"
                        },
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back"
                        )
                    }
                },
                actions = {
                    if (state is ViewBudgetScreenState.Loaded) {
                        IconButton(onClick = onConfigureClick) {
                            Icon(
                                imageVector = Icons.Default.Tune,
                                contentDescription = "Configure Budget",
                                tint = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            )
        }
    ) { innerPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
        ) {
            when (state) {
                is ViewBudgetScreenState.Loading -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator()
                    }
                }
                is ViewBudgetScreenState.Error -> {
                    Box(
                        modifier = Modifier.fillMaxSize().padding(16.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = state.message,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }
                }
                is ViewBudgetScreenState.Loaded -> {
                    LoadedBudgetDetail(
                        state = state,
                        onTransactionClick = onTransactionClick
                    )
                }
            }
        }
    }
}

@Composable
private fun LoadedBudgetDetail(
    state: ViewBudgetScreenState.Loaded,
    onTransactionClick: (String) -> Unit
) {
    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(16.dp),
        verticalArrangement = Arrangement.spacedBy(20.dp)
    ) {
        // Progress Card
        item {
            BudgetOverviewCard(
                budget = state.budget,
                progress = state.progress
            )
        }

        // Info Card (Categories and Dashboards)
        item {
            BudgetMetadataCard(
                budget = state.budget,
                linkedDashboards = state.dashboards
            )
        }

        // Spending Trend Graph Card
        item {
            BudgetTrendGraphCard(
                state = state
            )
        }

        // Transactions Header
        item {
            Text(
                text = "Transactions (Current Month)",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface,
                modifier = Modifier.padding(horizontal = 4.dp, vertical = 4.dp)
            )
        }

        // Transactions List or Empty State
        if (state.transactions.isEmpty()) {
            item {
                GlassCard(modifier = Modifier.fillMaxWidth()) {
                    Box(
                        modifier = Modifier.fillMaxWidth().padding(24.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = "No transactions found for this budget this month.",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }
            }
        } else {
            items(state.transactions, key = { it.transactionId }) { transaction ->
                val accountName = state.accountNames[transaction.accountId]
                val institutionName = state.institutionNames[transaction.accountId]
                TransactionRowItem(
                    transaction = transaction,
                    accountName = accountName,
                    institutionName = institutionName,
                    onClick = { onTransactionClick(transaction.transactionId) }
                )
            }
        }
    }
}

@Composable
private fun BudgetOverviewCard(
    budget: Budget,
    progress: BudgetProgress
) {
    GlassCard(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.fillMaxWidth().padding(16.dp)) {
            Text(
                text = "Monthly Spending",
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Spacer(modifier = Modifier.height(8.dp))

            val progressValue = progress.progress.coerceIn(0f, 1f)
            val indicatorColor = if (progress.progress > 1f) {
                MaterialTheme.colorScheme.error
            } else if (progress.progress > 0.8f) {
                MaterialTheme.colorScheme.error.copy(alpha = 0.7f)
            } else {
                MaterialTheme.colorScheme.primary
            }

            LinearProgressIndicator(
                progress = { progressValue },
                modifier = Modifier.fillMaxWidth().height(8.dp).clip(RoundedCornerShape(4.dp)),
                color = indicatorColor,
                trackColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f),
                drawStopIndicator = {},
            )

            Spacer(modifier = Modifier.height(12.dp))

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "$${progress.currentMonthSpending.formatAmount()} spent of $${budget.targetValue.formatAmount()}",
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.primary
                )

                if (progress.changePercentage != null) {
                    val isUp = progress.changePercentage > 0.0
                    val percentText = if (isUp) {
                        "+${progress.changePercentage.formatAmount()}%"
                    } else {
                        "${progress.changePercentage.formatAmount()}%"
                    }
                    val changeColor = if (isUp) {
                        MaterialTheme.colorScheme.error
                    } else {
                        Color(0xFF00C853)
                    }
                    Text(
                        text = "$percentText vs last mo.",
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.Bold,
                        color = changeColor
                    )
                } else {
                    Text(
                        text = "No previous spending",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.outline
                    )
                }
            }
        }
    }
}

@Composable
private fun BudgetMetadataCard(
    budget: Budget,
    linkedDashboards: List<Dashboard>
) {
    val cleanCategories = remember(budget.categories) {
        budget.categories.map { detailed ->
            val primary = CATEGORIES.find { it.second == detailed }?.first ?: ""
            cleanCategoryName(primary, detailed)
        }
    }

    GlassCard(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.fillMaxWidth().padding(16.dp)) {
            // Categories section
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    imageVector = Icons.Default.Category,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(20.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = "Categories",
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
            }
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = cleanCategories.joinToString(", "),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )

            Spacer(modifier = Modifier.height(16.dp))

            // Dashboards section
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    imageVector = Icons.Default.Dashboard,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(20.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = "Linked Dashboards",
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
            }
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = if (linkedDashboards.isEmpty()) "None" else linkedDashboards.joinToString(", ") { it.name },
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun BudgetTrendGraphCard(
    state: ViewBudgetScreenState.Loaded
) {
    var isCumulative by remember { mutableStateOf(true) }

    val today = remember { Clock.System.todayIn(TimeZone.currentSystemDefault()) }

    // Select which data to plot
    val currentData = if (isCumulative) state.currentMonthCumulative else state.currentMonthDaily
    val prevData = if (isCumulative) state.previousMonthCumulative else state.previousMonthDaily

    // For current month, only plot up to the current day of the month
    val currentPlotPoints = remember(currentData, today) {
        currentData.take(today.day)
    }

    val maxVal = remember(currentPlotPoints, prevData) {
        val m1 = currentPlotPoints.maxOrNull() ?: 0.0
        val m2 = prevData.maxOrNull() ?: 0.0
        maxOf(m1, m2, 10.0) // fallback to 10.0 if empty or zero
    }

    GlassCard(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.fillMaxWidth().padding(16.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "Spending Trend",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )

                // Toggle tabs
                Row(
                    modifier = Modifier
                        .clip(RoundedCornerShape(8.dp))
                        .background(Color(0x15FFFFFF))
                        .padding(2.dp),
                    horizontalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    val options = listOf(true to "Cumulative", false to "Daily")
                    options.forEach { (optionVal, label) ->
                        val isSelected = isCumulative == optionVal
                        val bg by animateColorAsState(
                            targetValue = if (isSelected) MaterialTheme.colorScheme.primary else Color.Transparent,
                            label = "tabBg"
                        )
                        val textCol by animateColorAsState(
                            targetValue = if (isSelected) Color(0xFF003731) else MaterialTheme.colorScheme.onSurfaceVariant,
                            label = "tabText"
                        )
                        Box(
                            modifier = Modifier
                                .clip(RoundedCornerShape(6.dp))
                                .background(bg)
                                .clickable { isCumulative = optionVal }
                                .padding(horizontal = 10.dp, vertical = 4.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = label,
                                style = MaterialTheme.typography.labelSmall,
                                fontWeight = FontWeight.Bold,
                                color = textCol
                            )
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(24.dp))

            // Line Graph Canvas
            val primaryColor = MaterialTheme.colorScheme.primary // Teal/Cyan for current month
            val secondaryColor = Color(0xFF8B5CF6) // Purple for previous month
            val gridColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.05f)
            val labelColor = MaterialTheme.colorScheme.onSurfaceVariant

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(180.dp)
            ) {
                Canvas(modifier = Modifier.fillMaxSize()) {
                    val width = size.width
                    val height = size.height

                    val paddingLeft = 0f
                    val paddingRight = 0f
                    val paddingTop = 10.dp.toPx()
                    val paddingBottom = 20.dp.toPx()

                    val graphWidth = width - paddingLeft - paddingRight
                    val graphHeight = height - paddingTop - paddingBottom

                    // 1. Draw horizontal grid lines and Y axis labels
                    val gridLines = 4
                    for (i in 0..gridLines) {
                        val y = paddingTop + graphHeight * (1f - i.toFloat() / gridLines)
                        drawLine(
                            color = gridColor,
                            start = Offset(paddingLeft, y),
                            end = Offset(width, y),
                            strokeWidth = 1.dp.toPx()
                        )
                    }

                    // Max days to show on X axis is 30 (representing days of month)
                    val maxDays = 30f

                    // 2. Draw Previous Month Line
                    if (prevData.isNotEmpty()) {
                        val prevPath = Path()
                        prevData.forEachIndexed { index, valAmt ->
                            val x = paddingLeft + (index.toFloat() / maxDays) * graphWidth
                            val y = paddingTop + graphHeight * (1f - (valAmt.toFloat() / maxVal.toFloat()))
                            if (index == 0) {
                                prevPath.moveTo(x, y)
                            } else {
                                prevPath.lineTo(x, y)
                            }
                        }
                        drawPath(
                            path = prevPath,
                            color = secondaryColor.copy(alpha = 0.5f),
                            style = Stroke(
                                width = 2.dp.toPx(),
                                pathEffect = PathEffect.dashPathEffect(
                                    floatArrayOf(10f, 10f), 0f
                                )
                            )
                        )
                    }

                    // 3. Draw Current Month Line (solid with subtle gradient fill below)
                    if (currentPlotPoints.isNotEmpty()) {
                        val currentPath = Path()
                        val fillPath = Path()

                        currentPlotPoints.forEachIndexed { index, valAmt ->
                            val x = paddingLeft + (index.toFloat() / maxDays) * graphWidth
                            val y = paddingTop + graphHeight * (1f - (valAmt.toFloat() / maxVal.toFloat()))
                            if (index == 0) {
                                currentPath.moveTo(x, y)
                                fillPath.moveTo(x, paddingTop + graphHeight)
                                fillPath.lineTo(x, y)
                            } else {
                                currentPath.lineTo(x, y)
                                fillPath.lineTo(x, y)
                            }

                            if (index == currentPlotPoints.lastIndex) {
                                fillPath.lineTo(x, paddingTop + graphHeight)
                            }
                        }

                        // Draw fill
                        if (currentPlotPoints.size > 1) {
                            drawPath(
                                path = fillPath,
                                brush = Brush.verticalGradient(
                                    colors = listOf(
                                        primaryColor.copy(alpha = 0.15f),
                                        Color.Transparent
                                    )
                                )
                            )
                        }

                        // Draw line
                        drawPath(
                            path = currentPath,
                            color = primaryColor,
                            style = Stroke(
                                width = 3.dp.toPx(),
                                cap = StrokeCap.Round
                            )
                        )

                        // Draw pulse/dot at the last current month point
                        val lastIndex = currentPlotPoints.lastIndex
                        val lastVal = currentPlotPoints.last()
                        val lastX = paddingLeft + (lastIndex.toFloat() / maxDays) * graphWidth
                        val lastY = paddingTop + graphHeight * (1f - (lastVal.toFloat() / maxVal.toFloat()))

                        drawCircle(
                            color = primaryColor.copy(alpha = 0.2f),
                            radius = 8.dp.toPx(),
                            center = Offset(lastX, lastY)
                        )
                        drawCircle(
                            color = primaryColor,
                            radius = 4.dp.toPx(),
                            center = Offset(lastX, lastY)
                        )
                    }
                }
            }

            // X-axis labels
            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text("Day 1", style = MaterialTheme.typography.labelSmall, color = labelColor)
                Text("Day 10", style = MaterialTheme.typography.labelSmall, color = labelColor)
                Text("Day 20", style = MaterialTheme.typography.labelSmall, color = labelColor)
                Text("Day 30", style = MaterialTheme.typography.labelSmall, color = labelColor)
            }

            Spacer(modifier = Modifier.height(20.dp))

            // Legend & Month-over-Month comparison details
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                // Current month
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.weight(1f)
                ) {
                    Box(
                        modifier = Modifier
                            .size(10.dp)
                            .background(primaryColor, CircleShape)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Column {
                        Text(
                            text = "${state.currentMonthName} (This Month)",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Text(
                            text = "$${state.totalCurrent.formatAmount()}",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                }

                // Previous month
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.weight(1f)
                ) {
                    Box(
                        modifier = Modifier
                            .size(10.dp)
                            .background(secondaryColor, CircleShape)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Column {
                        Text(
                            text = "${state.previousMonthName} (Last Month)",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Text(
                            text = "$${state.totalPrev.formatAmount()}",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                }
            }

            // Pacing comparison info
            val diff = state.totalCurrent - state.totalPrevUpToSameDay
            val percent = if (state.totalPrevUpToSameDay > 0) (abs(diff) / state.totalPrevUpToSameDay) * 100 else 0.0
            val pacingText = if (diff > 0) {
                "Spending is up ${percent.formatAmount()}% compared to the same point last month (${state.previousMonthName} ${today.day}: $${state.totalPrevUpToSameDay.formatAmount()})."
            } else if (diff < 0) {
                "Spending is down ${percent.formatAmount()}% compared to the same point last month (${state.previousMonthName} ${today.day}: $${state.totalPrevUpToSameDay.formatAmount()})."
            } else {
                "Spending is exactly pacing with last month at this point."
            }

            Spacer(modifier = Modifier.height(16.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(8.dp))
                    .background(if (diff > 0) MaterialTheme.colorScheme.error.copy(alpha = 0.05f) else MaterialTheme.colorScheme.primary.copy(alpha = 0.05f))
                    .border(1.dp, if (diff > 0) MaterialTheme.colorScheme.error.copy(alpha = 0.1f) else MaterialTheme.colorScheme.primary.copy(alpha = 0.1f), RoundedCornerShape(8.dp))
                    .padding(12.dp)
            ) {
                Text(
                    text = pacingText,
                    style = MaterialTheme.typography.bodySmall,
                    color = if (diff > 0) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.primary
                )
            }
        }
    }
}

@Composable
private fun TransactionRowItem(
    transaction: Transaction,
    accountName: String?,
    institutionName: String?,
    onClick: () -> Unit
) {
    val category = transaction.getRecentSpendingCategoryBreakdownKey()
    val icon = when {
        category.contains("Groceries", ignoreCase = true) || category.contains("Food", ignoreCase = true) -> Icons.Default.Group
        category.contains("Utilities", ignoreCase = true) || category.contains("Electric", ignoreCase = true) -> Icons.Default.Bolt
        category.contains("Income", ignoreCase = true) || category.contains("Transfer", ignoreCase = true) -> Icons.Default.AccountBalance
        else -> Icons.Default.Bolt
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(Color(0x06FFFFFF))
            .border(1.dp, Color(0x0AFFFFFF), RoundedCornerShape(8.dp))
            .clickable { onClick() }
            .padding(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(40.dp)
                .background(MaterialTheme.colorScheme.surfaceContainerHighest, CircleShape),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(20.dp)
            )
        }
        Spacer(modifier = Modifier.width(12.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = transaction.name,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )
            val details = listOfNotNull(accountName, institutionName).joinToString(" • ")
            if (details.isNotEmpty()) {
                Spacer(modifier = Modifier.height(2.dp))
                Text(
                    text = details,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                text = transaction.date,
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
        Spacer(modifier = Modifier.width(8.dp))
        Column(horizontalAlignment = Alignment.End) {
            val isExpense = transaction.amount > 0
            Text(
                text = "${if (isExpense) "-" else "+"}$${abs(transaction.amount).formatAmount()}",
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                color = if (isExpense) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.primary
            )
            Text(
                text = category,
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

private fun cleanCategoryName(primary: String, detailed: String): String {
    val cleanPrimary = primary.trim()
    val cleanDetailed = detailed.trim()
    val remaining = if (cleanPrimary.isNotEmpty() && cleanDetailed.startsWith(cleanPrimary)) {
        val suffix = cleanDetailed.removePrefix(cleanPrimary).removePrefix("_")
        if (suffix.isNotEmpty()) suffix else cleanPrimary
    } else {
        if (cleanDetailed.isNotEmpty()) cleanDetailed else "General"
    }

    return remaining.split("_", " ")
        .filter { it.isNotEmpty() }
        .joinToString(" ") { word ->
            word.lowercase().replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
        }
}

@PreviewLightDark
@Composable
private fun ViewBudgetScreenPreview(
    @PreviewParameter(ViewBudgetPreviewParameterProvider::class) state: ViewBudgetScreenState
) {
    AppTheme(
        darkTheme = isSystemInDarkTheme(),
        dynamicColor = false,
    ) {
        Surface(modifier = Modifier.fillMaxSize(), color = MaterialTheme.colorScheme.background) {
            ViewBudgetScreenContent(
                state = state,
                onConfigureClick = {},
                onNavigateBack = {},
                onTransactionClick = {}
            )
        }
    }
}
